import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import {
    noteUpdated,
    noteUpdatedAvatar,
    noteUpdatedBy,
    noteUpdatedHuman,
} from './formatTime';

dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

const LONDON_TIME_ZONE = 'Europe/London';
const DRF_DATE_PATTERN = /^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}:\d{2}:\d{2})(?:\.(\d+))?Z?$/;
const EXPLICIT_OFFSET_PATTERN = /(?:Z|[+-]\d{2}:?\d{2})$/i;
const CHANNEL_META = {
    WHATSAPP: { label: 'WhatsApp', icon: 'mdi:whatsapp', color: '#128C7E' },
    PHONE: { label: 'Phone', icon: 'mdi:phone-outline', color: '#007B55' },
    EMAIL: { label: 'Email', icon: 'mdi:email-outline', color: '#B76E00' },
    SMS: { label: 'SMS', icon: 'mdi:message-text-outline', color: '#C0266D' },
    OTHER: { label: 'Other', icon: 'mdi:dots-horizontal', color: '#637381' },
};

export const LAST_CONTACTED_ORDERING_FIELD = 'last_contacted_at';

export const resolveLastContactOrderingField = (field) => (
    field === 'lastContact' || field === 'daysSince'
        ? LAST_CONTACTED_ORDERING_FIELD
        : field
);

export const getLastContactChannelMeta = (channel) => {
    if (!channel) return null;
    return CHANNEL_META[String(channel).toUpperCase()] || CHANNEL_META.OTHER;
};

export const parseLastContactDate = (raw) => {
    if (!raw) return null;

    const value = String(raw);
    const drfMatch = value.match(DRF_DATE_PATTERN);
    if (drfMatch) {
        const [, day, month, year, time, fraction = ''] = drfMatch;
        const milliseconds = fraction.slice(0, 3).padEnd(3, '0');
        const parsed = dayjs.tz(
            `${year}-${month}-${day} ${time}.${milliseconds}`,
            'YYYY-MM-DD HH:mm:ss.SSS',
            LONDON_TIME_ZONE
        );
        return parsed.isValid() ? parsed : null;
    }

    const parsed = EXPLICIT_OFFSET_PATTERN.test(value)
        ? dayjs(value).tz(LONDON_TIME_ZONE)
        : dayjs.tz(value, LONDON_TIME_ZONE);
    return parsed.isValid() ? parsed : null;
};

export const formatLastContactDate = (raw, pattern = 'HH:mm DD/MM/YY') => {
    const parsed = parseLastContactDate(raw);
    return parsed ? parsed.format(pattern) : '';
};

export const formatLastContactHuman = (raw, now = undefined) => {
    const parsed = parseLastContactDate(raw);
    if (!parsed) return 'No contact';
    return now === undefined ? parsed.fromNow() : parsed.from(dayjs(now));
};

export const resolveLastContact = (item = {}) => {
    const hasCanonicalTimestamp = Object.prototype.hasOwnProperty.call(item, 'last_contacted_at');
    if (item.last_contacted_at) {
        const by = item.last_contacted_by
            ? `${item.last_contacted_by.first_name || ''} ${item.last_contacted_by.last_name || ''}`.trim()
            : '';
        return {
            raw: item.last_contacted_at,
            human: formatLastContactHuman(item.last_contacted_at),
            channel: item.last_contacted_channel || '',
            avatar: item.last_contacted_by?.avatar || '',
            by,
            isCanonical: true,
        };
    }

    if (item.lead_notes?.length > 0) {
        return {
            raw: noteUpdated(item.lead_notes),
            human: noteUpdatedHuman(item.lead_notes),
            channel: [...item.lead_notes].sort((a, b) => (
                parseLastContactDate(b?.updated_at)?.valueOf() || 0
            ) - (
                parseLastContactDate(a?.updated_at)?.valueOf() || 0
            ))[0]?.contacted_via || '',
            avatar: noteUpdatedAvatar(item.lead_notes),
            by: noteUpdatedBy(item.lead_notes),
            isCanonical: false,
        };
    }

    if (hasCanonicalTimestamp) {
        return {
            raw: null,
            human: 'No contact',
            channel: '',
            avatar: '',
            by: '',
            isCanonical: true,
        };
    }

    return {
        raw: null,
        human: 'No contact',
        channel: '',
        avatar: '',
        by: '',
        isCanonical: false,
    };
};

export const resolveTableLastContact = (item = {}, appointments = []) => {
    const resolved = resolveLastContact(item);
    if (resolved.isCanonical || resolved.raw) return resolved;

    const latestAppointment = [...appointments].sort((a, b) => (
        parseLastContactDate(b?.datetime)?.valueOf() || 0
    ) - (
        parseLastContactDate(a?.datetime)?.valueOf() || 0
    ))[0];
    if (!latestAppointment?.datetime) return resolved;

    return {
        ...resolved,
        raw: latestAppointment.datetime,
        human: formatLastContactHuman(latestAppointment.datetime),
    };
};
